import { type InitializationErrorTypes, type InitializationSteps } from '@/constants/basket';
import { type ToastRecoveryCategory } from '@/utils/toastErrorClassifier';

export enum OrderStatusTypes {
  OK = 'ok',
  WARNING = 'warning',
  ERROR = 'error',
  PAGE_ERROR = 'pageError',
  RECOVERY_502 = 'recovery502',
  UPDATING = 'updating',
  RECONCILIATION = 'reconciliation',
  TRANSMITTING = 'transmitting',
  TOAST_PAYMENT_RECOVERY = 'toastPaymentRecovery',
}

// Name is historical: the submit path fires on any isRetryableError (408/429/5xx,
// network drops, non-JSON gateway bodies), not just 502 — TEAM-14612. A rename would
// touch the action, reducer, MODAL constant and four test files, so it is a follow-up.
export type Recovery502Endpoint =
  | 'validate'
  | 'submit'
  | 'rewards_available'
  | 'rewards'
  | 'conveyance'
  // TEAM-15341: a committed wallet whose handoff re-save at Place Order moved the total. Not a
  // gateway failure — the re-save worked — so it gets its own copy rather than 'conveyance'.
  | 'conveyance_total_changed'
  | 'wanted_time';

export enum OrderStatuses {
  ORDER_NOT_INITIALIZED = 'orderNotInitialized',
  ORDER_PENDING = 'orderPending',

  INITIALIZING_ORDER = 'initializingOrder',
  INITIALIZING_ORDER_ERROR = 'initializingOrderError',
  VALIDATING_ORDER = 'validatingOrder',
  PAYMENT_TYPES_GETTING = 'paymentTypesGetting',
  PROMO_CODE_ADDING = 'promoCodeAdding',
  PROMO_CODE_REMOVING = 'promoCodeRemoving',
  BASKET_REWARDS_GETTING = 'basketRewardsGetting',
  BASKET_REWARDS_ADDING = 'basketRewardsAdding',
  BASKET_REWARDS_REMOVING = 'basketRewardsRemoving',
  TIP_ADDING = 'tipAdding',
  UTENSILS_SETTING = 'utensilsSetting',
  BASKET_INSTRUCTIONS_SETTING = 'basketInstructionsSetting',
  CONVEYANCE_SETTING = 'conveyanceSetting',
  CONVEYANCE_DELIVERY_SETTING = 'conveyanceDeliverySetting',
  STORED_VALUE_POSTING = 'storedValuePosting',
  WANTED_AT_BASKET_SETTING = 'wantedAtBasketSetting',
  WANTED_AT_BASKET_REMOVING = 'wantedAtBasketRemoving',
  GETTING_PAYMENT_TOKEN = 'gettingPaymentToken',
  GETTING_PAYMENT_TOKEN_ERROR = 'gettingPaymentTokenError',
  DIGITAL_WALLET_ORDER_STARTED = 'digitalWalletOrderStarted',
  ORDER_SUBMISSION_DIGITAL_WALLET_TRIGGERED = 'orderSubmissionDigitalWalletTriggered',
  ORDER_SUBMISSION_TRIGGERED = 'orderSubmissionTriggered',
  ORDER_SUBMISSION_PCI_FAILURE = 'orderSubmissionPciFailure',
  ORDER_SUBMISSION_STARTED = 'orderSubmissionStarted',
  PAYMENT_APPLYING = 'paymentApplying',
  ORDER_SUBMISSION_ERROR = 'orderSubmissionError',
  ORDER_TIME_UNAVAILABLE = 'orderTimeUnavailable',
  DELIVERY_UNAVAILABLE = 'deliveryUnavailable',
  RESTAURANT_UNAVAILABLE = 'restaurantUnavailable',
  /**
   * TEAM-14493: the restaurant's POS could not price the order (FPO code 13). Terminal — the
   * blocking modal offers Call restaurant and Back to menu, never a retry.
   */
  PRICING_UNAVAILABLE = 'pricingUnavailable',
  /**
   * TEAM-15690: coo-api answered 404 for the diner's checkout basket, so every further call for it
   * fails the same way. Terminal — the inline notice offers "Start a new cart", never a retry.
   */
  BASKET_NOT_FOUND = 'basketNotFound',

  ORDER_COMPLETE = 'orderComplete',
}

/**
 * The statuses in which nothing is updating, so a Place Order button shows its own label rather
 * than "Order updating....". TEAM-15690: BASKET_NOT_FOUND is one — the button is disabled beside
 * the basket-not-found notice.
 */
const ORDER_NOT_UPDATING_STATUSES: ReadonlySet<OrderStatuses> = new Set([
  OrderStatuses.ORDER_PENDING,
  OrderStatuses.ORDER_NOT_INITIALIZED,
  OrderStatuses.ORDER_SUBMISSION_PCI_FAILURE,
  OrderStatuses.ORDER_TIME_UNAVAILABLE,
  OrderStatuses.BASKET_NOT_FOUND,
]);

/** Whether a Place Order button should read "Order updating....". */
export const isOrderUpdating = (status: OrderStatuses): boolean =>
  !ORDER_NOT_UPDATING_STATUSES.has(status);

export enum OrderStatusMessages {
  // The only entry that does not reach the checkout status bar: its sole consumer
  // is `initializeOrderReconciliation` (redux/orderStatus/saga.ts), which sets
  // `type: RECONCILIATION`, and StatusBar suppresses that type. It renders in the
  // error-reconciliation modal (components/checkout/errorReconciliation), under a
  // header that already says "There was an issue starting your <type> order" and,
  // in the THROTTLING branch, directly above a working HandoffTimePicker. So the
  // next step is "try again" only — telling the diner to phone the restaurant
  // would contradict the recovery control rendered beneath it.
  INITIALIZING_ORDER_ERROR = "We couldn't start your order. Please try again.",
  BASKET_REWARDS_ADDING_ERROR = "We couldn't add that reward to your order. Please try again.",
  BASKET_REWARDS_REMOVING_ERROR = "We couldn't remove that reward from your order. Please try again.",
  TIP_ADDING_ERROR = "We couldn't add your tip. Please try again.",
  UTENSIL_SETTING_ERROR = "We couldn't save your utensil preference. Please try again.",
  BASKET_INSTRUCTIONS_SETTING_ERROR = "We couldn't save your order instructions. Please try again.",
  // Deliberately makes no claim about whether the card was charged, authorized or
  // held: a provider can place an authorization before a submit fails, so any
  // "your card hasn't been charged" promise is false whenever a pending hold lands
  // on the diner's statement. See TEAM-13883 / TEAM-13884.
  ORDER_SUBMISSION_ERROR = "We couldn't submit your order. Please try again — if it keeps failing, contact the restaurant directly.",
}

/**
 * TEAM-15690: which basket call answered 404. Carried on the action and tagged in Sentry
 * (`basket_not_found_source`), so the Spike (TEAM-15705) can tell where diners first hit it.
 * `validate` covers every validate outside initialization: the checkout page's, the pre-auth one
 * and the pre-resubmit one.
 */
export type BasketNotFoundSource = 'payment_types' | 'init_validate' | 'validate' | 'submit';

/** Optional link rendered alongside an error message (e.g. "Go to Account Settings"). */
export interface OrderStatusMessageAction {
  href: string;
  label: string;
}

/**
 * TEAM-14645: why a checkout submit failed, parsed from the error body in the submit saga
 * and carried onto order-status state so the Place Order button can decide whether a retry
 * can possibly succeed. Terminal causes (PAYMENT_DECLINED, DELIVERY_QUOTE) keep the button
 * disabled until the diner changes the card or address; the rest leave it available and
 * behave exactly as before.
 */
export enum SubmitFailureCause {
  // Terminal — no retry against the same card / address can succeed.
  PAYMENT_DECLINED = 'paymentDeclined',
  DELIVERY_QUOTE = 'deliveryQuote',
  // Retryable — a fresh validate (TEAM-14644) can make these succeed.
  FUNDS_TOTAL = 'fundsTotal',
  BASKET_NOT_VALID = 'basketNotValid',
  TOAST_PAYMENT_INTENT = 'toastPaymentIntent',
  // Anything the parsed body does not identify — keep today's behavior.
  UNCLASSIFIED = 'unclassified',
}

export interface IOrderStatusState {
  type: OrderStatusTypes;
  /**
   * Diner-safe copy. TEAM-13882: this is resolved by the render rule in `prepareErrorMessage` and
   * never contains raw upstream text, so it must not be used to classify anything — see
   * `initializationErrorType`.
   */
  message: string | null;
  /** Actionable link shown next to the error message in the StatusBar. */
  messageAction?: OrderStatusMessageAction | null;
  /**
   * How the checkout reconciliation modal should treat an initialization failure.
   *
   * TEAM-13882: the modal used to re-derive this by substring-matching `message` — the string it was
   * about to render — against `INITIALIZATION_ERRORS`. That coupled the diner's copy to the app's
   * behaviour, and the two had already silently diverged. `initializeOrderSaga` now computes it from
   * the raw technical detail and passes it here. We deliberately store the *classification* and not
   * the technical string: this slice is rendered in three places and is spread into `__NEXT_DATA__`
   * by the SSR `HYDRATE` merge, so keeping raw upstream text out of it entirely is a stronger
   * guarantee than promising not to render one field.
   */
  initializationErrorType?: InitializationErrorTypes | null;
  /**
   * TEAM-15138: which cart line coo-api's `required_option_missing` named, and which option group
   * it is missing. Only ever set alongside `InitializationErrorTypes.REQUIRED_OPTION`; `null`
   * otherwise, written on every reconciliation so one failure cannot inherit another's target.
   *
   * One object rather than two loose fields, because the two are meaningless apart and the action
   * creator already carries them together — flattening them here only created a shape the reducer
   * had to rebuild.
   *
   * `basketItemIndex` is an index into `basket.content.basket_items`, resolved by
   * `resolveRequiredOptionBasketItemIndex` before it reaches this slice — the modal receives an
   * answer, not an envelope to interpret. `null` there means the envelope identified no line, which
   * is the signal to fall back to the generic affordance.
   *
   * `groupName` is operator-authored text, rendered as a plain-text button label only: never as a
   * sentence, never through an innerHTML sink. It is not upstream *error* text, so the TEAM-13882
   * rule about keeping raw upstream strings out of this slice is not in tension with it — but it is
   * the one field here a reviewer should look twice at.
   */
  requiredOption?: { basketItemIndex: number | null; groupName: string | null } | null;
  status: OrderStatuses;
  utm_source?: string;
  recovery502Endpoint?: Recovery502Endpoint | null;
  toastRecoveryCategory?: ToastRecoveryCategory | null;
  toastConsecutiveFailures?: number;
  // TEAM-14645: the parsed submit-failure cause, gating the Place Order button.
  cause?: SubmitFailureCause | null;
  /**
   * True once a submit attempt on the current basket has failed and left the diner
   * back on a pressable Place Order button. Gates the pre-resubmit basket
   * re-validation so the first attempt is unchanged and only retries re-validate
   * (TEAM-14644). Cleared when the order is destroyed / re-initialized.
   */
  submitFailed?: boolean;
  /**
   * TEAM-14291: true when the failure that set this state occurred after a Toast
   * payment intent was authorized (toastPaymentConfirmed was true at the time of
   * the error). Used by the error surfaces to tell the diner that the hold on
   * their card is released automatically and their bank may take time to remove it.
   */
  toastPaymentWasAuthorized?: boolean;
  /**
   * Outcome of the pre-authorization re-validation a retry press runs before it lets a
   * payment flow commit an amount (TEAM-14644 UAT follow-up). `'succeeded'` is the ONLY
   * value that may authorize. Cleared once the submit it gated is triggered, when a fresh
   * failure invalidates it, and on a new checkout.
   */
  preAuthValidation?: PreAuthValidation | null;
  /**
   * TEAM-14787: whether an app-side validate has priced the current checkout basket. A basket
   * is created holding a zero total and coo-api writes the real one only on a successful
   * validate, so an unpriced basket and a basket validated at a genuine $0 (100% promo, full
   * reward) both carry `total === 0` — the number alone cannot tell them apart. This can.
   *
   * Keyed by basket id so a new basket is never mistaken for a priced one. `'priced'` is never
   * downgraded for the same id: a later refresh failing on a priced basket leaves its figures up
   * (the fail-closed rules for those refreshes are separate, TEAM-14666 / TEAM-14818). `null`
   * means no validate has settled yet on this page session — the reload / session-restore case,
   * which renders a spinner rather than an error.
   */
  basketPricing?: BasketPricing | null;
  /**
   * TEAM-15690: the basket coo-api answered 404 for — set with BASKET_NOT_FOUND, so the next 404 for
   * the same basket (every call for it fails that way) is not reported to Sentry again. Survives
   * INITIALIZE_ORDER, which can reuse the same dead basket id; DESTROY_ORDER and
   * ORDER_NOT_INITIALIZED reset it.
   */
  lostBasketId?: string;
}

/** TEAM-14787: see `IOrderStatusState.basketPricing`. */
export interface BasketPricing {
  basketId: string;
  outcome: 'priced' | 'failed';
  /**
   * The setup step that failed, when the failure came from `initializeOrderSaga` (AC3). A step
   * BEFORE `VALIDATE` — setting/clearing the wanted time or setting conveyance — left the server
   * basket without the diner's choice, so the order summary's Try again re-runs initialization
   * rather than only re-validating. That is the one path this field steers: any other successful
   * validate (guest form, tip, gift card) still upgrades the verdict to `'priced'`, because its prices
   * are real, and does not re-apply the step. Absent for checkout-page validates.
   */
  failedStep?: InitializationSteps | null;
}

/**
 * `'intent_rotated'` is a SUCCESS that still cannot authorize: the basket refreshed fine, but
 * the total moved, and coo-api mints a new Toast payment intent whenever it does. The card the
 * diner applied was collected against the old intent, so there is nothing left to confirm and
 * they have to enter payment again. Kept distinct from `'failed'` because the basket is good —
 * only the payment instrument is stale (TEAM-14644 UAT follow-up).
 */
export type PreAuthValidation = 'pending' | 'succeeded' | 'intent_rotated' | 'failed';
